import { useState, useEffect } from "react";
import { auth, db, storage } from "../firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { collection, query, where, getDocs } from "firebase/firestore";
import "../styles/EditListing.css";
// Remove or update the CSS import based on your project structure
// import "../styles/AddListing.css"; 

const EditListing = ({ listingId, onBack }) => {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    price: "",
    location: "",
    category1: "",       // Changed from category to category1
    category2: "",       // Added category2 field
    condition: "",       // Added condition field as required by the automation
    campaignId: "",
    photos: []
  });

  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [newPhotos, setNewPhotos] = useState([]);
  const [deletedPhotos, setDeletedPhotos] = useState([]);
  const [subcategories, setSubcategories] = useState([]);

  // Define the main categories and their subcategories
  const categoryOptions = {
    "Furniture": ["Armoires & Wardrobes", "Beds & Bed Frames", "Benches", "Bookcases", "Coffee Tables", "Dressers", "Mattresses", "Sofas, Loveseats & Sectional", "TV Stands & Entertainment Centers", "Wall Shelves & Ledges"]
  };

  // Condition options for the dropdown
  const conditionOptions = [
    "New", 
    "Used - Like New", 
    "Used - Good", 
    "Used - Fair"
  ];

  useEffect(() => {
    const fetchData = async () => {
      if (!auth.currentUser || !listingId) return;

      try {
        // Fetch the listing data
        const listingDoc = await getDoc(doc(db, "listings", listingId));
        
        if (listingDoc.exists()) {
          const listingData = listingDoc.data();
          
          // Check if we have the old format with just "category"
          let category1 = listingData.category1;
          let category2 = listingData.category2;
          
          // If we have old format, convert it
          if (!category1 && listingData.category) {
            category1 = listingData.category;
          }

          // Update state with the listing data
          setFormData({
            title: listingData.title || "",
            description: listingData.description || "",
            price: listingData.price || "",
            location: listingData.location || "",
            category1: category1 || "",
            category2: category2 || "",
            condition: listingData.condition || "",
            campaignId: listingData.campaignId || "",
            photos: listingData.photos || []
          });
          
          // Set subcategories based on category1
          if (category1 && categoryOptions[category1]) {
            setSubcategories(categoryOptions[category1]);
          }
        } else {
          setMessage({ text: "Listing not found.", type: "error" });
        }

        // Fetch campaigns for dropdown
        const campaignQuery = query(collection(db, "campaigns"), where("userId", "==", auth.currentUser.uid));
        const campaignSnapshot = await getDocs(campaignQuery);
        const campaignsData = campaignSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setCampaigns(campaignsData);
      } catch (error) {
        console.error("Error fetching data:", error);
        setMessage({ text: "Error loading listing data.", type: "error" });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [listingId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === "category1") {
      // When category1 changes, reset category2 and update subcategories
      setFormData(prev => ({
        ...prev,
        category1: value,
        category2: ""  // Reset subcategory when main category changes
      }));
      
      // Update available subcategories
      if (categoryOptions[value]) {
        setSubcategories(categoryOptions[value]);
      } else {
        setSubcategories([]);
      }
    } else {
      // Handle other field changes
      setFormData(prev => ({
        ...prev,
        [name]: name === "price" ? parseFloat(value) || value : value
      }));
    }
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    setNewPhotos(files);
  };

  const handlePhotoDelete = (photoUrl) => {
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.filter(url => url !== photoUrl)
    }));
    setDeletedPhotos([...deletedPhotos, photoUrl]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!auth.currentUser) return;

    setLoading(true);
    setMessage({ text: "", type: "" });

    try {
      // Upload new photos
      const uploadPromises = newPhotos.map(file => {
        const photoRef = ref(storage, `listings/${auth.currentUser.uid}/${listingId}/${file.name}`);
        return uploadBytes(photoRef, file)
          .then(() => getDownloadURL(photoRef));
      });

      const newPhotoUrls = await Promise.all(uploadPromises);
      
      // Delete removed photos from storage
      const deletePromises = deletedPhotos.map(photoUrl => {
        // Extract the file path from the URL
        const fileRef = ref(storage, photoUrl);
        return deleteObject(fileRef).catch(error => {
          console.error("Error deleting photo:", error);
          // Continue with the update even if photo deletion fails
          return null;
        });
      });
      
      await Promise.all(deletePromises);

      // Update the listing in Firestore
      const listingRef = doc(db, "listings", listingId);
      await updateDoc(listingRef, {
        ...formData,
        photos: [...formData.photos, ...newPhotoUrls],
        updatedAt: new Date(),
      });

      setMessage({ text: "Listing updated successfully!", type: "success" });
      setNewPhotos([]);
      setDeletedPhotos([]);
      
      // Add a delay before going back to give the user a chance to see the success message
      setTimeout(() => {
        onBack(); // Navigate back to listing view
      }, 1500);
    } catch (error) {
      console.error("Error updating listing:", error);
      setMessage({ text: "Error updating listing. Please try again.", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  if (loading && message.text === "") {
    return <div className="loading">Loading listing data...</div>;
  }

  return (
    <div className="add-listing-container">
      <h2>Edit Listing</h2>
      
      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}
      
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="title">Title</label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            required
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows="4"
            required
          ></textarea>
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="price">Price ($)</label>
            <input
              type="number"
              id="price"
              name="price"
              value={formData.price}
              onChange={handleChange}
              min="0"
              step="0.01"
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="location">Location</label>
            <input
              type="text"
              id="location"
              name="location"
              value={formData.location}
              onChange={handleChange}
              required
            />
          </div>
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="category1">Main Category</label>
            <select
              id="category1"
              name="category1"
              value={formData.category1}
              onChange={handleChange}
              required
            >
              <option value="">Select a category</option>
              {Object.keys(categoryOptions).map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>
          
          <div className="form-group">
            <label htmlFor="category2">Subcategory</label>
            <select
              id="category2"
              name="category2"
              value={formData.category2}
              onChange={handleChange}
              disabled={!formData.category1}
            >
              <option value="">Select a subcategory</option>
              {subcategories.map(subcategory => (
                <option key={subcategory} value={subcategory}>{subcategory}</option>
              ))}
            </select>
          </div>
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="condition">Condition</label>
            <select
              id="condition"
              name="condition"
              value={formData.condition}
              onChange={handleChange}
              required
            >
              <option value="">Select condition</option>
              {conditionOptions.map(condition => (
                <option key={condition} value={condition}>{condition}</option>
              ))}
            </select>
          </div>
          
          <div className="form-group">
            <label htmlFor="campaignId">Campaign</label>
            <select
              id="campaignId"
              name="campaignId"
              value={formData.campaignId}
              onChange={handleChange}
            >
              <option value="">None (No Campaign)</option>
              {campaigns.map(campaign => (
                <option key={campaign.id} value={campaign.id}>{campaign.name}</option>
              ))}
            </select>
          </div>
        </div>
        
        <div className="form-group">
          <label htmlFor="photos">Add Photos</label>
          <input
            type="file"
            id="photos"
            name="photos"
            onChange={handlePhotoUpload}
            multiple
            accept="image/*"
          />
        </div>
        
        {/* Display current photos with delete option */}
        {formData.photos.length > 0 && (
          <div className="current-photos">
            <h4>Current Photos</h4>
            <div className="photo-grid">
              {formData.photos.map((photoUrl, index) => (
                <div key={index} className="photo-item">
                  <img src={photoUrl} alt={`Listing #${index}`} />
                  <button 
                    type="button" 
                    className="delete-photo" 
                    onClick={() => handlePhotoDelete(photoUrl)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Display newly selected photos */}
        {newPhotos.length > 0 && (
          <div className="new-photos">
            <h4>New Photos to Upload</h4>
            <ul>
              {newPhotos.map((file, index) => (
                <li key={index}>{file.name}</li>
              ))}
            </ul>
          </div>
        )}
        
        <div className="form-actions">
          <button type="button" onClick={onBack} className="cancel-button">
            Cancel
          </button>
          <button type="submit" className="submit-button" disabled={loading}>
            {loading ? "Updating..." : "Update Listing"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditListing;